import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MemberTypeClient, MemberTypeDTO } from '../services/apiClient';
import { API_BASE_URL, MAX_DROPDOWN_PAGE_SIZE } from '../config/config';
import axiosInstance from '../services/axiosInstance';

const apiClient = new MemberTypeClient(API_BASE_URL, axiosInstance);

// Query hook - Get all member types for dropdowns (cached forever)
export const useMemberTypes = () => {
  return useQuery({
    queryKey: ['memberTypes'],
    queryFn: async () => {
      const response = await apiClient.searchMemberTypes({
        page: 1,
        pageSize: MAX_DROPDOWN_PAGE_SIZE,
        sortBy: 'DisplayPosition',
      });
      return response?.data?.results || [];
    },
    staleTime: 4 * 60 * 60 * 1000, // 4 hours
    refetchOnReconnect: true,
  });
};

// Direct API search (no caching) - for admin tables with server-side paging
export const useMemberTypeSearch = () => {
  const search = async (params: {
    page: number;
    pageSize: number;
    sortBy?: string;
    sortDescending?: boolean;
    filters?: any;
  }) => {
    const response = await apiClient.searchMemberTypes({
      page: params.page,
      pageSize: params.pageSize,
      sortBy: params.sortBy || 'CreatedUtc',
      sortDescending: params.sortDescending ?? true,
      filters: params.filters || {},
    });
    return {
      results: response?.data?.results || [],
      totalCount: response?.data?.totalCount || 0,
    };
  };

  return { search };
};

// Query hook - Get single member type by ID
export const useMemberTypeById = (id: string) => {
  return useQuery({
    queryKey: ['memberTypes', id],
    queryFn: async () => {
      const response = await apiClient.getMemberTypeById(id);
      return response?.data;
    },
    enabled: !!id,
    staleTime: Infinity,
    refetchOnReconnect: true,
  });
};

// Mutation hook - Create member type
export const useCreateMemberType = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: MemberTypeDTO) => {
      await apiClient.createMemberType(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['memberTypes'] });
    },
  });
};

// Mutation hook - Update member type
export const useUpdateMemberType = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: MemberTypeDTO }) => {
      await apiClient.updateMemberType(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['memberTypes'] });
    },
  });
};

// Mutation hook - Delete member type
export const useDeleteMemberType = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.deleteMemberType(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['memberTypes'] });
    },
  });
};